import { useFormContext, useFormState } from "react-hook-form";
import { useSelector } from "react-redux";

import type { DatasetT } from "../api/types";
import type { StateT } from "../app/reducers";
import { exists } from "../common/helpers/exists";
import { useDatasetId } from "../dataset/selectors";
import QueryRunner from "../query-runner/QueryRunner";
import { useStartQuery, useStopQuery } from "../query-runner/actions";
import { QueryRunnerStateT } from "../query-runner/reducer";

import { Form } from "./config-types";
import {
  selectFormConfig,
  selectQueryRunner,
  selectRunningQuery,
} from "./stateSelectors";
import transformQueryToApi from "./transformQueryToApi";

const isButtonEnabled = ({
  queryRunner,
  datasetId,
  isValid,
}: {
  datasetId: DatasetT["id"] | null;
  queryRunner: QueryRunnerStateT | null;
  isValid: boolean;
}) => {
  if (!queryRunner) return false;

  return !!(
    datasetId !== null &&
    !queryRunner.startQuery.loading &&
    !queryRunner.stopQuery.loading &&
    isValid
  );
};

const FormQueryRunner = () => {
  const datasetId = useDatasetId();
  const queryRunner = useSelector<StateT, QueryRunnerStateT | null>(
    selectQueryRunner,
  );
  const queryId = useSelector<StateT, string | null>(selectRunningQuery);
  const isQueryRunning = exists(queryId);
  const formConfig = useSelector<StateT, Form | null>(selectFormConfig);

  const { getValues } = useFormContext();
  const { isValid } = useFormState();

  const buttonEnabled =
    exists(formConfig) &&
    isButtonEnabled({
      datasetId,
      queryRunner,
      isValid,
    });

  const startExternalFormsQuery = useStartQuery("externalForms");
  const stopExternalFormsQuery = useStopQuery("externalForms");

  const startQuery = () => {
    if (datasetId && exists(formConfig)) {
      const values = getValues();

      startExternalFormsQuery(
        datasetId,
        transformQueryToApi(formConfig, values),
      );
    }
  };
  const stopQuery = () => {
    if (queryId) {
      stopExternalFormsQuery(queryId);
    }
  };

  if (!queryRunner) {
    return null;
  }

  return (
    <QueryRunner
      queryRunner={queryRunner}
      disabled={!buttonEnabled}
      isQueryRunning={isQueryRunning}
      startQuery={startQuery}
      stopQuery={stopQuery}
    />
  );
};

export default FormQueryRunner;
